/**
 * Страница автоответов: управление правилами автоответов (TODO-040)
 * Дата: 29.01.2026 - Создана для новой системы автоответов
 * Дата: 30.01.2026 - Добавлена колонка "Клиника", унифицированные SearchInput/FilterSelect
 * Дата: 30.01.2026 - Рефакторинг под унифицированные UI компоненты (TODO-045)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/auto-replies.ts - API для работы с автоответами
 * - internal/handler/auto_reply.go - бэкенд: один автоответ — один канал (09.10.26)
 * - components/ui/SearchInput.tsx - унифицированный поиск с debounce
 * - components/ui/FilterSelect.tsx - унифицированный выпадающий фильтр
 * - components/ui/StatusBadge.tsx - унифицированный статус
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * - components/ui/ActionButtons.tsx - унифицированные кнопки действий
 * - components/ui/TenantSelector.tsx - выбор тенанта для SuperAdmin
 * 
 * КАК РАБОТАЕТ:
 * - Отображает список правил автоответов с фильтрацией
 * - Создание/редактирование/удаление правил
 * - Привязка шаблонов к правилам
 * - Фильтрация по типу триггера и активности
 * 
 * СХЕМА:
 * AutoRepliesPage → autoRepliesApi → backend API
 * AutoRepliesPage → SearchInput → фильтрация filteredRules
 * AutoRepliesPage → FilterSelect → фильтрация по типу/активности
 * AutoRepliesPage → ActionButtons → handleDelete/openEditModal
 */
import { useState, useEffect, useMemo } from 'react';
import { logger } from '@/utils/logger';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { autoRepliesApi, previewAutoReplyText, AutoReplyRule, TriggerType } from '@/api/auto-replies';
import { templatesApi, Template } from '@/api/templates';
import { fetchAllPages } from '@/api/paging';
import { settingsApi } from '@/api/settings';
import { adminApi } from '@/api/admin';
import { 
  Button, 
  Input, 
  Card, 
  CardContent, 
  Badge, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableHeader, 
  TableCell, 
  Modal, 
  Select, 
  Textarea, 
  TenantSelector, 
  SearchInput, 
  FilterSelect, 
  filterPresets,
  EmptyState,
  LoadingState,
  ActionButtons
} from '@/components/ui';
import { MainLayout } from '@/components/layout/MainLayout';
import { FlowNote } from '@/features/monitoring/FlowNote';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useToast } from '@/hooks/useToast';
import { Plus, AlertTriangle } from 'lucide-react';
import { ButtonTriggerSelector } from '@/components/features/auto-replies/ButtonTriggerSelector';
import { getChannelDisplayName, getChannelLabel, normalizeChannelId, normalizeChannelList } from '@/config/channels';
import {
  autoReplySectionRegistry,
  getAutoReplyContextLabel,
  getAutoReplySectionBadgeClass,
  getAutoReplySectionConfig,
  getRecommendedTriggerTypes,
  inferAutoReplySection,
  type AutoReplySectionId,
} from '@/features/auto-replies/sectionRegistry';

const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  feedback: 'Обратная связь',
  first_message: 'Первое сообщение',
  keyword: 'Ключевые слова',
  regex: 'Регулярное выражение',
  waba_button: 'Кнопка WABA шаблона',
  tg_callback: 'Inline callback',
  inbound_first_contact: '📩 Первичное обращение', // TODO-061
};

const TRIGGER_TYPE_COLORS: Record<TriggerType, string> = {
  feedback: 'bg-green-100 text-green-700',
  first_message: 'bg-blue-100 text-blue-700',
  keyword: 'bg-purple-100 text-purple-700',
  regex: 'bg-orange-100 text-orange-700',
  waba_button: 'bg-teal-100 text-teal-700',
  tg_callback: 'bg-indigo-100 text-indigo-700',
  inbound_first_contact: 'bg-emerald-100 text-emerald-700', // TODO-061
};

function getTriggerTypeLabel(rule: AutoReplyRule): string {
  if (rule.trigger_type !== 'tg_callback') {
    return TRIGGER_TYPE_LABELS[rule.trigger_type];
  }

  const channels = normalizeChannelList(rule.channels);
  const hasTelegram = channels.includes('telegram_bot');
  const hasMax = channels.includes('max');

  if (hasMax && !hasTelegram) {
    return 'Кнопка MAX inline';
  }
  if (hasTelegram && !hasMax) {
    return 'Кнопка TG inline';
  }
  if (hasTelegram && hasMax) {
    return 'Inline callback (TG/MAX)';
  }

  return TRIGGER_TYPE_LABELS[rule.trigger_type];
}

function humanTrigger(rule: AutoReplyRule): string {
  switch (rule.trigger_type) {
    case 'keyword': {
      const words = rule.pattern.split('|').map((part) => part.trim()).filter(Boolean).slice(0, 3);
      return words.length > 0 ? `слова: ${words.join(', ')}` : 'ключевые слова';
    }
    case 'regex':
      return 'регулярное выражение';
    case 'waba_button':
      return rule.waba_button_ids?.length ? `кнопка: ${rule.waba_button_ids.slice(0, 2).join(', ')}` : 'кнопка WABA';
    case 'tg_callback':
      return 'нажатие inline-кнопки';
    case 'feedback':
      return 'ответ на сбор обратной связи';
    case 'first_message':
      return 'первое сообщение';
    case 'inbound_first_contact':
      return 'первичное обращение';
    default:
      return getTriggerTypeLabel(rule);
  }
}

function getTriggerTypeColor(rule: AutoReplyRule): string {
  if (rule.trigger_type !== 'tg_callback') {
    return TRIGGER_TYPE_COLORS[rule.trigger_type];
  }

  const channels = normalizeChannelList(rule.channels);
  const hasTelegram = channels.includes('telegram_bot');
  const hasMax = channels.includes('max');

  if (hasMax && !hasTelegram) {
    return 'bg-purple-100 text-purple-700';
  }
  if (hasTelegram && !hasMax) {
    return 'bg-indigo-100 text-indigo-700';
  }
  if (hasTelegram && hasMax) {
    return 'bg-violet-100 text-violet-700';
  }

  return TRIGGER_TYPE_COLORS[rule.trigger_type];
}

const AUTO_REPLY_CHANNEL_FILTERS = [
  'whatsapp_regular',
  'whatsapp_waba',
  'telegram_client',
  'telegram_bot',
  'max',
  'max_client',
  'sms',
  'sms_tel',
] as const;

export function AutoRepliesPage() {
  const { tenantId: tenantIdForApi, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  const { messengerStatus } = useTenantConfig(tenantIdForApi);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AutoReplyRule | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [triggerTypeFilter, setTriggerTypeFilter] = useState<TriggerType | ''>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [channelFilter, setChannelFilter] = useState('');
  const [activeSectionFilter, setActiveSectionFilter] = useState<AutoReplySectionId>('all');
  const [showGuide, setShowGuide] = useState(false);
  const [probeText, setProbeText] = useState('');
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [editorSectionId, setEditorSectionId] = useState<AutoReplySectionId>('all');
  const [templates, setTemplates] = useState<Template[]>([]);

  // Загрузка правил
  const { data: rulesData, isLoading } = useQuery({
    queryKey: ['auto-replies', tenantIdForApi, triggerTypeFilter, activeFilter],
    // 09.10.26. Не удалять по требованию владельца системы.
    // limit=500 на API становится 50, имена шаблонов тогда остаются UUID.
    queryFn: async () => {
      const filters = {
        trigger_type: triggerTypeFilter || undefined,
        is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
      };
      const page = await fetchAllPages((offset, limit) =>
        autoRepliesApi.listRules(tenantIdForApi, filters, { offset, limit }).then((data) => ({
          items: data.rules || [],
          total: data.total,
        })),
      );
      return { rules: page.items, total: page.total };
    },
    enabled: !requireTenantSelection,
  });

  const rules = rulesData?.rules || [];

  // Загрузка шаблонов для выбора
  useEffect(() => {
    if (requireTenantSelection) {
      setTemplates([]);
      return;
    }
    fetchAllPages((offset, limit) =>
      templatesApi.list({ tenantId: tenantIdForApi, offset, limit }).then((data) => ({
        items: data.templates || [],
        total: data.total,
      })),
    )
      .then((page) => setTemplates(page.items))
      .catch(() => setTemplates([]));
  }, [tenantIdForApi, requireTenantSelection]);

  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'auto-replies' });
  }, []);

  // Создание правила
  const createMutation = useMutation({
    mutationFn: (data: Omit<AutoReplyRule, 'id' | 'tenant_id' | 'created_at' | 'updated_at'> & { tenant_id?: string }) =>
      autoRepliesApi.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-replies'] });
      showSuccess('Правило создано');
      setIsCreateModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Ошибка при создании правила');
    },
  });

  // Обновление правила
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AutoReplyRule> }) =>
      autoRepliesApi.updateRule(id, data, tenantIdForApi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-replies'] });
      showSuccess('Правило обновлено');
      setEditingRule(null);
      resetForm();
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Ошибка при обновлении правила');
    },
  });

  // Удаление правила
  const deleteMutation = useMutation({
    mutationFn: (id: string) => autoRepliesApi.deleteRule(id, tenantIdForApi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-replies'] });
      showSuccess('Правило удалено');
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Ошибка при удалении правила');
    },
  });

  const [formData, setFormData] = useState<Partial<AutoReplyRule>>({
    name: '',
    description: '',
    trigger_type: 'keyword',
    pattern: '',
    channels: ['whatsapp_regular'],
    template_id: '',
    waba_trigger_template_id: undefined,
    waba_button_ids: undefined,
    tg_trigger_template_id: undefined,
    tg_callback_data: undefined,
    save_feedback: false,
    notify_operator: false,
    sync_to_crm: false,
    priority: 0,
    is_active: true,
    inbound_config: undefined, // TODO-061
  });

  // Список тенантов для отображения колонки "Клиника" (30.01.2026)
  interface TenantInfo { id: string; name: string; slug: string; }
  const [tenantsMap, setTenantsMap] = useState<Record<string, TenantInfo>>({});

  // Загрузка тенантов для SuperAdmin
  useEffect(() => {
    if (!isSuperAdmin) return;
    adminApi.listTenants().then((data) => {
      const map: Record<string, TenantInfo> = {};
      data.tenants.forEach((t: { id: string; name: string; slug: string; }) => {
        map[t.id] = { id: t.id, name: t.name, slug: t.slug };
      });
      setTenantsMap(map);
    }).catch(() => {});
  }, [isSuperAdmin]);

  // Настройки каналов тенанта для проверки доступности
  // Дата: 30.01.2026 — Для показа предупреждений о недоступных каналах
  const [channelConfig, setChannelConfig] = useState<{
    whatsapp_enabled: boolean;
    whatsapp_waba: boolean;
    telegram_enabled: boolean;
    max_enabled: boolean;
    channel_priority: string[];
  }>({
    whatsapp_enabled: false,
    whatsapp_waba: false,
    telegram_enabled: false,
    max_enabled: false,
    channel_priority: [],
  });

  // Загрузка конфигурации каналов из channel_priority
  useEffect(() => {
    if (requireTenantSelection) {
      return;
    }
    settingsApi.getDeliverySettings(tenantIdForApi).then((data) => {
      const channels = normalizeChannelList(data.channel_priority || []);
      setChannelConfig({
        whatsapp_enabled: channels.includes('whatsapp_regular'),
        whatsapp_waba: channels.includes('whatsapp_waba'),
        telegram_enabled: channels.some(c => c === 'telegram_bot' || c === 'telegram_client'),
        max_enabled: channels.includes('max'),
        channel_priority: channels,
      });
    }).catch(() => {});
  }, [tenantIdForApi, requireTenantSelection]);

  // Фильтрация шаблонов по выбранным каналам
  // 28.02.2026 — FIX UX: для триггеров по кнопкам (WABA/TG) шаблон автоответа
  // может быть текстовым, поэтому не ограничиваем выбор только WABA-шаблонами.
  const filteredTemplates = useMemo(() => {
    const selectedChannels = formData.channels || [];
    const hasWaba = selectedChannels.includes('whatsapp_waba');
    const activeTemplates = templates.filter((t) => t.is_active);

    // Для кнопочных триггеров шаблон автоответа может быть и текстовым,
    // поэтому не ограничиваем выбор только WABA-шаблонами.
    if (formData.trigger_type === 'waba_button' || formData.trigger_type === 'tg_callback') {
      return activeTemplates;
    }
    
    return activeTemplates.filter((t) => {
      
      if (hasWaba) {
        return t.template_type === 'waba_template' || t.external_template_id || t.messenger === 'whatsapp_waba';
      }
      
      return t.template_type !== 'waba_template' && !t.external_template_id && t.messenger !== 'whatsapp_waba';
    });
  }, [templates, formData.channels, formData.trigger_type]);

  const activeWabaTemplates = useMemo(
    () => templates.filter((template) => template.is_active && (template.template_type === 'waba_template' || !!template.external_template_id)),
    [templates],
  );

  const resetForm = () => {
    setEditorSectionId('all');
    setFormData({
      name: '',
      description: '',
      trigger_type: 'keyword',
      pattern: '',
      channels: ['whatsapp_regular'],
      template_id: '',
      waba_trigger_template_id: undefined,
      waba_button_ids: undefined,
      waba_response_template_id: undefined,
      tg_trigger_template_id: undefined,
      tg_callback_data: undefined,
      save_feedback: false,
      notify_operator: false,
      sync_to_crm: false,
      priority: 0,
      is_active: true,
      inbound_config: undefined, // TODO-061
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditorSectionId('all');
    setIsCreateModalOpen(true);
  };

  const openEditModal = (rule: AutoReplyRule) => {
    setEditingRule(rule);
    setEditorSectionId(inferAutoReplySection(rule));
    setFormData({
      name: rule.name,
      description: rule.description,
      trigger_type: rule.trigger_type,
      pattern: rule.pattern,
      channels: normalizeChannelList(rule.channels),
      template_id: rule.template_id,
      waba_trigger_template_id: rule.waba_trigger_template_id,
      waba_button_ids: rule.waba_button_ids,
      waba_response_template_id: rule.waba_response_template_id,
      tg_trigger_template_id: rule.tg_trigger_template_id,
      tg_callback_data: rule.tg_callback_data,
      save_feedback: rule.save_feedback,
      notify_operator: rule.notify_operator,
      sync_to_crm: rule.sync_to_crm,
      priority: rule.priority,
      is_active: rule.is_active,
      after_event_type: rule.after_event_type,
      within_hours: rule.within_hours,
      inbound_config: rule.inbound_config, // TODO-061
    });
    setIsCreateModalOpen(true);
  };

  const handleEditorSectionChange = (sectionId: AutoReplySectionId) => {
    setEditorSectionId(sectionId);
    const section = getAutoReplySectionConfig(sectionId);
    const normalizedCurrent = (formData.after_event_type || '').trim().toLowerCase();
    const nextContext = section.contexts.find((item) => item.value === normalizedCurrent)?.value
      || section.contexts[0]?.value
      || undefined;

    setFormData((prev) => ({
      ...prev,
      after_event_type: nextContext,
      within_hours: nextContext ? prev.within_hours : undefined,
    }));
  };

  const handleSave = () => {
    // Валидация для разных типов триггеров
    if (!formData.name || !formData.template_id) {
      showError('Заполните все обязательные поля');
      return;
    }
    
    // Для waba_button и tg_callback паттерн не обязателен
    if (formData.trigger_type !== 'waba_button' && formData.trigger_type !== 'tg_callback') {
      if (!formData.pattern) {
        showError('Заполните паттерн');
        return;
      }
    }
    
    // Для waba_button проверяем наличие выбранных кнопок
    if (formData.trigger_type === 'waba_button') {
      if (!formData.waba_trigger_template_id || !formData.waba_button_ids || formData.waba_button_ids.length === 0) {
        showError('Выберите шаблон и хотя бы одну кнопку WABA');
        return;
      }
    }
    
    // Для tg_callback проверяем наличие выбранных кнопок
    if (formData.trigger_type === 'tg_callback') {
      if (!formData.tg_trigger_template_id || !formData.tg_callback_data || formData.tg_callback_data.length === 0) {
        showError('Выберите шаблон и хотя бы одну кнопку TG');
        return;
      }
    }

    const data = {
      ...formData,
      channels: normalizeChannelList(formData.channels),
      tenant_id: isSuperAdmin ? (selectedTenantId || undefined) : undefined,
    } as Omit<AutoReplyRule, 'id' | 'tenant_id' | 'created_at' | 'updated_at'> & { tenant_id?: string };

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить это правило?')) return;
    deleteMutation.mutate(id);
  };

  const handleToggleActive = (rule: AutoReplyRule) => {
    updateMutation.mutate({
      id: rule.id,
      data: { is_active: !rule.is_active },
    });
  };

  const filteredRules = rules.filter((rule) => {
    const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rule.pattern.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTriggerType = !triggerTypeFilter || rule.trigger_type === triggerTypeFilter;
    const matchesActive =
      activeFilter === 'all' ||
      (activeFilter === 'active' && rule.is_active) ||
      (activeFilter === 'inactive' && !rule.is_active);
    const normalizedChannels = normalizeChannelList(rule.channels);
    const matchesChannel = !channelFilter || normalizedChannels.includes(channelFilter);
    const matchesSection = activeSectionFilter === 'all' || inferAutoReplySection(rule) === activeSectionFilter;
    return matchesSearch && matchesTriggerType && matchesActive && matchesChannel && matchesSection;
  });
  const textProbe = useMemo(() => previewAutoReplyText(filteredRules, probeText), [filteredRules, probeText]);
  const selectedRule = filteredRules.find((rule) => rule.id === selectedRuleId) ?? null;

  const getTemplateName = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    return template?.name || templateId;
  };

  const getTriggerTemplateSummary = (rule: AutoReplyRule) => {
    if (rule.waba_trigger_template_id) {
      return `WABA trigger: ${getTemplateName(rule.waba_trigger_template_id)}`;
    }
    if (rule.tg_trigger_template_id) {
      return `TG/MAX trigger: ${getTemplateName(rule.tg_trigger_template_id)}`;
    }
    return '';
  };

  const renderResponseTemplateCell = (rule: AutoReplyRule) => {
    const primaryTemplateName = getTemplateName(rule.template_id);
    const hasDedicatedWabaResponse = !!rule.waba_response_template_id;
    const dedicatedWabaTemplateName = hasDedicatedWabaResponse ? getTemplateName(rule.waba_response_template_id as string) : '';
    const triggerSummary = getTriggerTemplateSummary(rule);

    return (
      <div className="space-y-1">
        <div className="font-medium">
          {hasDedicatedWabaResponse ? dedicatedWabaTemplateName : primaryTemplateName}
        </div>
        <div className="flex flex-wrap gap-1">
          {hasDedicatedWabaResponse ? (
            <Badge variant="info" className="text-xs">
              WABA ответ
            </Badge>
          ) : null}
          <Badge variant="secondary" className="text-xs">
            text fallback
          </Badge>
        </div>
        {hasDedicatedWabaResponse && rule.waba_response_template_id !== rule.template_id ? (
          <div className="text-xs text-gray-500">Fallback: {primaryTemplateName}</div>
        ) : null}
        {triggerSummary ? (
          <div className="text-xs text-gray-500">{triggerSummary}</div>
        ) : null}
      </div>
    );
  };

  const editorSection = getAutoReplySectionConfig(editorSectionId);
  const recommendedTriggerTypes = getRecommendedTriggerTypes(editorSectionId);

  return (
    <MainLayout headerTitle="Автоответы">
      <div className="space-y-3 p-4" data-testid="page-auto-replies">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {isSuperAdmin && (
              <div className="w-64">
                <TenantSelector value={selectedTenantId || ''} onChange={setSelectedTenant} />
              </div>
            )}
            <button type="button" className="shell-button-secondary h-9" onClick={() => setShowGuide((open) => !open)}>
              Справка
            </button>
          </div>
          <Button onClick={openCreateModal} testId="btn-create-rule" disabled={requireTenantSelection}>
            <Plus className="w-4 h-4 mr-2" />
            Создать правило
          </Button>
        </div>
        {showGuide && <FlowNote id="autoreply" />}

        <Card>
          <CardContent className="space-y-3 pt-4">
            <div className="flex flex-wrap gap-3">
              {/* SearchInput — переиспользуемый компонент поиска с debounce */}
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Поиск по названию или паттерну..."
                className="flex-1"
                testId="input-search-rules"
              />
              {/* FilterSelect — унифицированный выпадающий фильтр (тип триггера) */}
              <FilterSelect
                value={triggerTypeFilter}
                onChange={(v) => setTriggerTypeFilter(v as TriggerType | '')}
                options={filterPresets.autoReplyTriggers}
                placeholder="Все типы"
                testId="select-trigger-type-filter"
                className="w-48"
              />
              {/* FilterSelect — фильтр активности */}
              <FilterSelect
                value={activeFilter}
                onChange={(v) => setActiveFilter(v as 'all' | 'active' | 'inactive')}
                options={[
                  { value: 'all', label: 'Все' },
                  { value: 'active', label: 'Активные' },
                  { value: 'inactive', label: 'Неактивные' },
                ]}
                showAllOption={false}
                testId="select-active-filter"
                className="w-40"
              />
              <FilterSelect
                value={channelFilter}
                onChange={setChannelFilter}
                options={AUTO_REPLY_CHANNEL_FILTERS.map((channel) => ({
                  value: channel,
                  label: getChannelLabel(channel),
                }))}
                placeholder="Все каналы"
                testId="select-channel-filter"
                className="w-44"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <input
                value={probeText}
                onChange={(event) => setProbeText(event.target.value)}
                placeholder="Проверить текст без отправки"
                className="h-9 min-w-[240px] flex-1 rounded-lg border border-shell-border bg-shell-bg px-3 text-sm"
              />
              <p className="text-xs leading-4 text-shell-muted">Только keyword и regex. Кнопки этим текстом не запускаются. Приоритет сервера не меняется.</p>
            </div>
            {probeText.trim() && (
              <div className="rounded-lg border border-shell-border bg-shell-elevated px-3 py-2 text-sm text-shell-text">
                {textProbe.length === 0 ? (
                  <p>Совпадений среди активных keyword и regex нет.</p>
                ) : (
                  <ul className="space-y-1">
                    {textProbe.map((hit) => (
                      <li key={hit.ruleId}>{hit.name}: {hit.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {selectedRule && (
              <div className="rounded-lg border border-shell-border bg-shell-surface px-3 py-2">
                <p className="text-sm font-semibold text-shell-strong">{selectedRule.name}</p>
                <p className="mt-1 text-sm text-shell-text">{humanTrigger(selectedRule)}</p>
                <p className="mt-2 break-all font-mono text-xs text-shell-muted">{selectedRule.pattern || 'Паттерн не задан'}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {autoReplySectionRegistry.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSectionFilter(section.id)}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeSectionFilter === section.id
                      ? 'border-transparent bg-shell-strong text-shell-bg shadow-sm'
                      : 'border-shell-border bg-shell-surface text-shell-muted hover:border-shell-primary/30 hover:text-shell-text'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>

            <p className="text-xs leading-4 text-shell-muted">
              {getAutoReplySectionConfig(activeSectionFilter).description}
            </p>

            {/* Таблица правил */}
            {requireTenantSelection ? (
              <EmptyState
                type="noData"
                title="Выберите клинику"
                description="Выберите клинику для просмотра правил автоответов"
              />
            ) : isLoading ? (
              <LoadingState />
            ) : filteredRules.length === 0 ? (
              <EmptyState 
                type="search"
                title="Правила не найдены"
                description={searchQuery || triggerTypeFilter || activeFilter !== 'all' || channelFilter || activeSectionFilter !== 'all' ? 'Попробуйте изменить параметры поиска' : 'Создайте первое правило автоответа'}
                actionText="Создать правило"
                onAction={openCreateModal}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Название</TableHeader>
                    <TableHeader>Тип</TableHeader>
                    <TableHeader>Условие</TableHeader>
                    <TableHeader>Каналы</TableHeader>
                    <TableHeader>Ответ / trigger</TableHeader>
                    {/* Колонка "Клиника" для SuperAdmin (30.01.2026) */}
                    {isSuperAdmin && <TableHeader>Клиника</TableHeader>}
                    <TableHeader>Приоритет</TableHeader>
                    <TableHeader>Активно</TableHeader>
                    <TableHeader>Действия</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRules.map((rule) => (
                    <TableRow key={rule.id} className={`cursor-pointer ${selectedRuleId === rule.id ? 'bg-shell-primary-soft' : ''}`} onClick={() => setSelectedRuleId(rule.id)}>
                      <TableCell>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-medium">{rule.name}</div>
                            <Badge className={getAutoReplySectionBadgeClass(inferAutoReplySection(rule))}>
                              {getAutoReplySectionConfig(inferAutoReplySection(rule)).label}
                            </Badge>
                            {rule.after_event_type && (
                              <Badge variant="secondary" className="text-xs">
                                {getAutoReplyContextLabel(rule.after_event_type)}
                              </Badge>
                            )}
                            {rule.within_hours ? (
                              <Badge variant="secondary" className="text-xs">
                                {rule.within_hours} ч
                              </Badge>
                            ) : null}
                          </div>
                          {rule.description && (
                            <div className="text-sm text-shell-muted">{rule.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getTriggerTypeColor(rule)}>
                          {getTriggerTypeLabel(rule)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-shell-text" title={rule.pattern}>{humanTrigger(rule)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {rule.channels.map((ch) => {
                            const normalized = normalizeChannelId(ch);
                            return (
                              <Badge key={ch} variant="secondary" className="text-xs">
                                {getChannelDisplayName(normalized)}
                              </Badge>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell>{renderResponseTemplateCell(rule)}</TableCell>
                      {/* Колонка "Клиника" для SuperAdmin (30.01.2026) */}
                      {isSuperAdmin && (
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {tenantsMap[rule.tenant_id]?.name || rule.tenant_id?.slice(0, 8) || '—'}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell>{rule.priority}</TableCell>
                      <TableCell>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rule.is_active}
                            onChange={() => handleToggleActive(rule)}
                            data-testid={`switch-active-${rule.id}`}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">{rule.is_active ? 'Да' : 'Нет'}</span>
                        </label>
                      </TableCell>
                      <TableCell>
                        <ActionButtons
                          onEdit={() => openEditModal(rule)}
                          onDelete={() => handleDelete(rule.id)}
                          testIdPrefix={`rule-${rule.id}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Модальное окно создания/редактирования */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingRule(null);
          resetForm();
        }}
        title={editingRule ? 'Редактировать правило' : 'Создать правило'}
        testId="modal-rule-editor"
        size="lg"
        footer={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setIsCreateModalOpen(false);
                setEditingRule(null);
                resetForm();
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              isLoading={createMutation.isPending || updateMutation.isPending}
              testId="btn-save-rule"
            >
              Сохранить
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Название *</label>
            <Input
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Приветствие новым пациентам"
              testId="input-rule-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Описание</label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Описание правила"
              rows={2}
              testId="textarea-rule-description"
            />
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1">Раздел сценария</label>
                <Select
                  value={editorSectionId}
                  onChange={(e) => handleEditorSectionChange(e.target.value as AutoReplySectionId)}
                  options={autoReplySectionRegistry.map((section) => ({ value: section.id, label: section.label }))}
                  testId="select-rule-section"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Контекст исходящей рассылки</label>
                <Select
                  value={formData.after_event_type || ''}
                  onChange={(e) => {
                    const value = e.target.value || undefined;
                    setFormData((prev) => ({
                      ...prev,
                      after_event_type: value,
                      within_hours: value ? prev.within_hours : undefined,
                    }));
                  }}
                  options={[
                    { value: '', label: editorSection.contexts.length === 0 ? 'Без привязки к outbound-сценарию' : 'Выберите контекст' },
                    ...editorSection.contexts.map((item) => ({ value: item.value, label: item.label })),
                  ]}
                  testId="select-after-event-type"
                />
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-600">
              {editorSection.description}
              {formData.after_event_type && (
                <span>
                  {' '}Контекст нужен, чтобы правило матчило не любой входящий текст, а только ответ на нужный outbound event.
                </span>
              )}
            </div>
            {formData.after_event_type && (
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Окно срабатывания, часы</label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.within_hours ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      setFormData((prev) => ({
                        ...prev,
                        within_hours: raw === '' ? undefined : Math.max(0, parseInt(raw, 10) || 0),
                      }));
                    }}
                    placeholder="Например: 48"
                    testId="input-within-hours"
                  />
                </div>
                <div className="text-xs text-slate-600">
                  Рекомендуемые trigger types: {recommendedTriggerTypes.map((type) => TRIGGER_TYPE_LABELS[type]).join(', ')}.
                  Если поле пустое, правило проверит любой предыдущий исходящий этого контекста без ограничения по часам.
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Тип триггера *</label>
            <Select
              value={formData.trigger_type || 'keyword'}
              onChange={(e) => {
                const newType = e.target.value as TriggerType;
                const updates: Partial<AutoReplyRule> = { ...formData, trigger_type: newType };
                // TODO-061: инициализация inbound_config при выборе inbound_first_contact
                if (newType === 'inbound_first_contact' && !formData.inbound_config) {
                  updates.inbound_config = {
                    check_all_channels: true,
                    cooldown_hours: 24,
                    working_hours_only: false,
                    working_hours_start: '08:00',
                    working_hours_end: '21:00',
                    keyword_filter: '',
                    exclude_known_patients: false,
                    max_auto_replies_per_day: 3,
                  };
                  updates.pattern = '*'; // Паттерн не используется, но поле required в БД
                }
                if (newType !== 'inbound_first_contact') {
                  updates.inbound_config = undefined;
                }
                setFormData(updates);
              }}
              options={Object.entries(TRIGGER_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
              testId="select-trigger-type"
            />
          </div>

          {/* Дата: 07.02.2026 - ButtonTriggerSelector для waba_button и tg_callback */}
          {(formData.trigger_type === 'waba_button' || formData.trigger_type === 'tg_callback') ? (
            <ButtonTriggerSelector
              triggerType={formData.trigger_type}
              formData={formData}
              onChange={setFormData}
              tenantId={tenantIdForApi}
              testId="button-trigger-selector"
            />
          ) : formData.trigger_type === 'inbound_first_contact' ? (
            /* TODO-061: Inbound First-Contact — настройки вместо паттерна */
            <div className="space-y-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm font-medium text-emerald-800">
                📩 Настройки первичного обращения
              </p>
              <p className="text-xs text-emerald-600">
                Автоответ сработает только если пациент написал первым и мы ещё не отправляли ему сообщений.
              </p>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.inbound_config?.check_all_channels ?? true}
                  onChange={(e) => setFormData({
                    ...formData,
                    inbound_config: {
                      ...formData.inbound_config!,
                      check_all_channels: e.target.checked,
                    },
                  })}
                  className="w-4 h-4"
                  data-testid="checkbox-check-all-channels"
                />
                <span className="text-sm">Проверять исходящие по всем каналам</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.inbound_config?.working_hours_only ?? false}
                  onChange={(e) => setFormData({
                    ...formData,
                    inbound_config: {
                      ...formData.inbound_config!,
                      working_hours_only: e.target.checked,
                    },
                  })}
                  className="w-4 h-4"
                  data-testid="checkbox-working-hours-only"
                />
                <span className="text-sm">Только в рабочее время</span>
              </label>

              {formData.inbound_config?.working_hours_only && (
                <div className="flex gap-2 items-center">
                  <label className="text-sm">с</label>
                  <input
                    type="time"
                    value={formData.inbound_config?.working_hours_start || '08:00'}
                    onChange={(e) => setFormData({
                      ...formData,
                      inbound_config: { ...formData.inbound_config!, working_hours_start: e.target.value },
                    })}
                    className="rounded border border-gray-300 px-2 py-1 text-sm"
                    data-testid="input-working-hours-start"
                  />
                  <label className="text-sm">до</label>
                  <input
                    type="time"
                    value={formData.inbound_config?.working_hours_end || '21:00'}
                    onChange={(e) => setFormData({
                      ...formData,
                      inbound_config: { ...formData.inbound_config!, working_hours_end: e.target.value },
                    })}
                    className="rounded border border-gray-300 px-2 py-1 text-sm"
                    data-testid="input-working-hours-end"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Фильтр по ключевым словам (через |)</label>
                <Input
                  value={formData.inbound_config?.keyword_filter || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    inbound_config: { ...formData.inbound_config!, keyword_filter: e.target.value },
                  })}
                  placeholder="стоимость|цена|записаться|прием"
                  testId="input-keyword-filter"
                />
                <p className="text-xs text-gray-500 mt-1">Оставьте пустым для срабатывания на любое сообщение</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Cooldown (часы)</label>
                <Input
                  type="number"
                  value={formData.inbound_config?.cooldown_hours ?? 24}
                  onChange={(e) => setFormData({
                    ...formData,
                    inbound_config: { ...formData.inbound_config!, cooldown_hours: parseInt(e.target.value) || 0 },
                  })}
                  testId="input-cooldown-hours"
                />
                <p className="text-xs text-gray-500 mt-1">Не отправлять повторный автоответ в течение N часов</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Макс. автоответов в день (одному пациенту)</label>
                <Input
                  type="number"
                  value={formData.inbound_config?.max_auto_replies_per_day ?? 3}
                  onChange={(e) => setFormData({
                    ...formData,
                    inbound_config: { ...formData.inbound_config!, max_auto_replies_per_day: parseInt(e.target.value) || 1 },
                  })}
                  testId="input-max-auto-replies-per-day"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Паттерн *</label>
              <Input
                value={formData.pattern || ''}
                onChange={(e) => setFormData({ ...formData, pattern: e.target.value })}
                placeholder={
                  formData.trigger_type === 'regex'
                    ? '^привет|здравствуй$'
                    : formData.trigger_type === 'keyword'
                    ? 'привет, здравствуйте'
                    : formData.trigger_type === 'feedback'
                    ? '^[1-5]$'
                    : ''
                }
                testId="input-rule-pattern"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.trigger_type === 'regex' && 'Регулярное выражение'}
                {formData.trigger_type === 'keyword' && 'Ключевые слова через |'}
                {formData.trigger_type === 'feedback' && 'Паттерн для оценки (например: ^[4-5]$)'}
                {formData.trigger_type === 'first_message' && 'Автоматически для первого сообщения'}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Канал *</label>
            <p className="text-xs text-neutral-500 mb-2">Один автоответ — один канал. Похожий ответ на другом канале заводится отдельным правилом.</p>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-4">
                {[
                  { key: 'whatsapp_regular', enabled: channelConfig.whatsapp_enabled },
                  { key: 'whatsapp_waba', enabled: channelConfig.whatsapp_waba },
                  { key: 'telegram_client', enabled: channelConfig.telegram_enabled },
                  { key: 'telegram_bot', enabled: channelConfig.telegram_enabled },
                  { key: 'max', enabled: channelConfig.max_enabled },
                  { key: 'max_client', enabled: messengerStatus.max_client },
                  { key: 'sms', enabled: messengerStatus.sms },
                  { key: 'sms_tel', enabled: messengerStatus.sms_tel },
                ].map(({ key, enabled }) => (
                  <label key={key} className={`flex items-center gap-2 ${!enabled ? 'opacity-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.channels?.includes(key) || false}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          channels: e.target.checked ? [key] : [],
                          template_id: '',
                          waba_response_template_id: undefined,
                        });
                      }}
                      data-testid={`checkbox-channel-${key}`}
                    />
                    <span className="text-sm">{getChannelLabel(key)}</span>
                    {!enabled && <span className="text-xs text-orange-500">(не настроен)</span>}
                  </label>
                ))}
              </div>
              {formData.channels?.includes('whatsapp_regular') && !channelConfig.whatsapp_enabled && (
                <div className="flex items-center gap-2 text-orange-600 text-xs bg-orange-50 p-2 rounded">
                  <AlertTriangle className="w-4 h-4" />
                  <span>WhatsApp Regular не настроен для этого тенанта.</span>
                </div>
              )}
              {formData.channels?.includes('whatsapp_waba') && !channelConfig.whatsapp_waba && (
                <div className="flex items-center gap-2 text-orange-600 text-xs bg-orange-50 p-2 rounded">
                  <AlertTriangle className="w-4 h-4" />
                  <span>WhatsApp WABA не настроен для этого тенанта.</span>
                </div>
              )}
              {formData.channels?.includes('whatsapp_waba') && channelConfig.whatsapp_waba && (
                <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                  {/* 28.02.2026 — уточнение шага настройки для кнопочного WABA-триггера */}
                  ℹ️ Для триггера «Кнопка WABA шаблона» сначала выберите шаблон-источник и кнопку, затем шаблон автоответа.
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Шаблон ответа *</label>
            <Select
              value={formData.template_id || ''}
              onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
              options={[
                { value: '', label: 'Выберите шаблон' },
                ...filteredTemplates.map((template) => ({ 
                  value: template.id, 
                  label: template.external_template_id 
                    ? `${template.name} (WABA: ${template.external_template_id})`
                    : template.name 
                })),
              ]}
              testId="select-template"
            />
            {filteredTemplates.length === 0 && (
              <p className="text-xs text-orange-500 mt-1">
                Нет доступных шаблонов для выбранных каналов. 
                {channelConfig.whatsapp_waba && formData.channels?.includes('whatsapp_regular') && ' Создайте WABA-шаблон в Meta Business.'}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Это основной шаблон ответа и text fallback. Для `whatsapp_waba` можно дополнительно указать отдельный Meta/WABA response template.
            </p>
          </div>

          {formData.channels?.includes('whatsapp_waba') && (
            <div>
              <label className="block text-sm font-medium mb-1">WABA шаблон ответа</label>
              <Select
                value={formData.waba_response_template_id || ''}
                onChange={(e) => setFormData({ ...formData, waba_response_template_id: e.target.value || undefined })}
                options={[
                  { value: '', label: 'Не задан (использовать шаблон ответа как fallback)' },
                  ...activeWabaTemplates.map((template) => ({
                    value: template.id,
                    label: template.external_template_id
                      ? `${template.name} (WABA: ${template.external_template_id})`
                      : template.name,
                  })),
                ]}
                testId="select-waba-response-template"
              />
              <p className="mt-1 text-xs text-gray-500">
                Если поле заполнено, для `whatsapp_waba` runtime сначала попытается отправить именно этот Meta template, а `Шаблон ответа`
                останется text fallback и UI-связью для reply chain.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Приоритет</label>
            <Input
              type="number"
              value={formData.priority || 0}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              testId="input-rule-priority"
            />
            <p className="text-xs text-gray-500 mt-1">Чем выше число, тем выше приоритет</p>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.save_feedback || false}
                onChange={(e) => setFormData({ ...formData, save_feedback: e.target.checked })}
                data-testid="checkbox-save-feedback"
                className="w-4 h-4"
              />
              <span className="text-sm">Сохранять обратную связь</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.notify_operator || false}
                onChange={(e) => setFormData({ ...formData, notify_operator: e.target.checked })}
                data-testid="checkbox-notify-operator"
                className="w-4 h-4"
              />
              <span className="text-sm">Уведомлять оператора</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.sync_to_crm || false}
                onChange={(e) => setFormData({ ...formData, sync_to_crm: e.target.checked })}
                data-testid="checkbox-sync-to-crm"
                className="w-4 h-4"
              />
              <span className="text-sm">Синхронизировать в CRM</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active !== false}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                data-testid="switch-rule-active"
                className="w-4 h-4"
              />
              <span className="text-sm">Активно</span>
            </label>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
}
